import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useDispatch } from 'react-redux';

import moment from 'moment';

import { showMessage } from 'actions';
import { snackbarSuccess } from 'actions/snackbarMessage';
import useReduxMutation from 'hooks/useReduxMutation';
import { actionReminderSend } from 'sagas/sagaWatchersReminder';

import Button from './Button';
import Dialog, { DialogProps } from './Dialog';
import DialogActions from './DialogActions';
import DialogContent from './DialogContent';
import DialogTitle from './DialogTitle';
import Typography from './Typography';

const messages = defineMessages({
  title: {
    id: 'components.v2.DialogReminderSend.title',
    defaultMessage: 'Send reminder now?',
  },
  contentText1: {
    id: 'components.v2.DialogReminderSend.contentText1',
    defaultMessage: 'This reminder is scheduled to be sent at {sendAt}.',
  },
  contentText2: {
    id: 'components.v2.DialogReminderSend.contentText2',
    defaultMessage: 'Would you like to send it now instead?',
  },
  cancel: {
    id: 'components.v2.DialogReminderSend.cancel',
    defaultMessage: 'Do nothing',
  },
  submit: {
    id: 'components.v2.DialogReminderSend.submit',
    defaultMessage: 'Yes, send now',
  },
  messageSuccess: {
    id: 'components.v2.DialogReminderSend.messageSuccess',
    defaultMessage: 'Reminder has been successfully queued for immediate delivery',
  },
  messageError: {
    id: 'components.v2.DialogReminderSend.messageError',
    defaultMessage: 'There was a problem sending the reminder. Please try again',
  },
});

type TReminder = {
  _id: string;
  sendAt: string;
};

export interface DialogReminderSendProps extends Omit<DialogProps, 'classes' | 'onClose' | 'onSubmit'> {
  reminder: TReminder;
  onClose: () => void;
  onSuccess?: () => void;
}

const DialogReminderSend: React.FC<DialogReminderSendProps> = ({ reminder, onClose, onSuccess, ...rest }) => {
  const { formatMessage } = useIntl();

  // WORKAROUND use Redux typesafe-actions until we introduce GQL mutations
  const [executeSend, isPending] = useReduxMutation(actionReminderSend);
  const dispatch = useDispatch();

  const handleSubmit = () => {
    executeSend({
      method: 'put',
      url: `/reminders/${reminder._id}/rfn`,
      data: { sendAt: new Date(), status: 'scheduled' },
      callbackSuccess: () => {
        onClose();
        if (onSuccess) onSuccess();
        dispatch(snackbarSuccess(formatMessage(messages.messageSuccess)));
      },
      callbackError: () => dispatch(showMessage(formatMessage(messages.messageError), 'error')),
    });
  };

  return (
    <Dialog fullWidth onClose={onClose} {...rest}>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          {formatMessage(messages.contentText1, { sendAt: moment(reminder.sendAt as string).format('LLL') })}
        </Typography>
        <Typography variant="body1" paragraph>
          {formatMessage(messages.contentText2)}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="primary" data-lh-id="button-dialog-reminder-send-cancel" onClick={onClose}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          variant="contained"
          color="primary"
          data-lh-id="button-dialog-reminder-send-submit"
          onClick={handleSubmit}
          disabled={isPending}
        >
          {formatMessage(messages.submit)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogReminderSend;
